import { Controller } from "@hotwired/stimulus"

const REDUCED = "(prefers-reduced-motion: reduce)"

/**
 * Search, typing itself.
 *
 * Results are real rows rendered by the server; each carries the terms it
 * matches in `data-lp-terms`. As the query is typed, rows that stop matching
 * drop out and the result count follows. Nothing is fetched — this is the
 * shape of search, not a live query.
 */
export default class extends Controller {
  static targets = ["query", "row", "count", "empty", "caret"]

  static values = {
    queries: { type: String, default: "" },
    charDelay: { type: Number, default: 68 },
    hold: { type: Number, default: 2400 }
  }

  connect() {
    if (window.matchMedia(REDUCED).matches) return

    this.cancelled = false
    this.visible = true
    this.terms = this.queriesValue
      .split("|")
      .map((q) => q.trim())
      .filter(Boolean)

    if (this.terms.length === 0) return

    // Measure each row once, at its natural height, before anything collapses.
    this.rowTargets.forEach((row) => {
      row.dataset.lpH = String(row.getBoundingClientRect().height)
      row.style.maxHeight = `${row.dataset.lpH}px`
    })

    this.observer = new IntersectionObserver(
      ([entry]) => (this.visible = entry.isIntersecting),
      { threshold: 0, rootMargin: "-15% 0px -15% 0px" }
    )
    this.observer.observe(this.element)

    this.run()
  }

  disconnect() {
    this.cancelled = true
    if (this.observer) this.observer.disconnect()
    // Leave the list whole, with no inline heights holding it at a stale size.
    this.rowTargets.forEach((row) => {
      row.classList.remove("is-hidden")
      row.style.maxHeight = ""
    })
  }

  async run() {
    let i = 0
    while (!this.cancelled) {
      await this.idleUntilVisible()
      if (this.cancelled) return

      const term = this.terms[i % this.terms.length]
      await this.typeQuery(term)
      if (this.cancelled) return

      await this.sleep(this.holdValue)
      await this.clearQuery()
      await this.sleep(420)
      i++
    }
  }

  async typeQuery(term) {
    this.setCaret(true)
    for (let i = 0; i <= term.length; i++) {
      if (this.cancelled) return
      const value = term.slice(0, i)
      this.write(value)
      this.filter(value)
      await this.sleep(this.charDelayValue)
    }
    this.setCaret(false)
  }

  async clearQuery() {
    const current = this.hasQueryTarget ? this.queryTarget.textContent : ""
    this.setCaret(true)
    for (let i = current.length; i >= 0; i--) {
      if (this.cancelled) return
      const value = current.slice(0, i)
      this.write(value)
      this.filter(value)
      await this.sleep(this.charDelayValue * 0.45)
    }
    this.setCaret(false)
  }

  write(value) {
    if (this.hasQueryTarget) this.queryTarget.textContent = value
  }

  setCaret(on) {
    if (this.hasCaretTarget) this.caretTarget.classList.toggle("is-on", on)
  }

  filter(value) {
    const needle = value.trim().toLowerCase()
    let shown = 0

    this.rowTargets.forEach((row) => {
      const haystack = (row.dataset.lpTerms || row.textContent || "").toLowerCase()
      const match = needle.length === 0 || haystack.includes(needle)
      row.classList.toggle("is-hidden", !match)
      row.style.maxHeight = match ? `${row.dataset.lpH || 0}px` : "0px"
      if (match) shown++
    })

    if (this.hasCountTarget) {
      this.countTarget.textContent = needle.length === 0 ? "everything" : `${shown} match${shown === 1 ? "" : "es"}`
    }
    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("is-shown", shown === 0)
    }
  }

  async idleUntilVisible() {
    while (!this.visible && !this.cancelled) await this.sleep(300)
  }

  sleep(ms) {
    return new Promise((resolve) => {
      const id = setTimeout(resolve, ms)
      if (this.cancelled) {
        clearTimeout(id)
        resolve()
      }
    })
  }
}
